/**
 * Why-this-beats-that — compact comparative explainer for the shortlist.
 *
 * Decision-first output (not paragraph copy):
 *   - Top 3 suburbs only.
 *   - Per suburb: 2 short positives + 1 short tradeoff.
 *   - Plain English, no marketing filler ("well-rounded", "sits comfortably",
 *     "is better if…" all banned).
 *
 * Shape:
 *   WhyLine { id, name, positives: [string, string], tradeoff: string }
 *
 * Scoring is NOT touched. Axis selection (`scoreAxes`,
 * `pickDistinguishingAxis`) is the same as before — only the phrasing and
 * the output shape changed. Shortlist ranking lives upstream in
 * `auto-shortlist` and is untouched here.
 */

import type { Verdict } from "../data/verdicts";
import type { Priority } from "./constraint-priority";
import type { BudgetBand } from "./budget-band";
import type { CityCode } from "./budget-format";
import type { PurchaseMode } from "./purchase-mode";
import type { PropertyType } from "./property-type";
import { extractFacts } from "./suburb-facts";
import type { PriceBand } from "./suburb-facts";
import { getSchoolFact } from "./school-facts";
import {
  budgetRangeLabel,
  clampedPriceRange,
  formatMidpoint,
} from "./budget-format";
import { getBudgetBucket } from "./budget-band";
import {
  getPublicSchoolRanks,
  getPublicSchoolRankRange,
  getPrivateSchoolRanks,
  hasSchoolRank,
  SCHOOL_RANK_UNKNOWN_LINE,
  PRIVATE_ONLY_NOTE,
} from "../data/school-ranks";
import {
  proximitySummary,
  proximityLine,
  proximityBadge,
  type ProximityTier,
} from "./hyd-school-proximity";
import { getActiveDataset } from "./active-dataset";

/* -------------------------------------------------------------------------- */
/*  Public types                                                              */
/* -------------------------------------------------------------------------- */

export interface WhyContext {
  priority: Priority;
  budgetBand: BudgetBand;
  city: CityCode;
  purchaseMode: PurchaseMode;
  propertyType: PropertyType;
}

/** School-badge tint bucket — drives the pill colour only. */
export type SchoolBadgeTone = "strong" | "mixed" | "weak" | "unknown";

export interface WhyLine {
  id: string;
  name: string;
  /** Two strongest positives — short plain-English phrases. */
  positives: [string, string];
  /** One main friction — short plain-English phrase. */
  tradeoff: string;
  /** Full rendered text for the school badge. Under School priority this
   *  is rank-first ("Schools: Rank 4–9 (VIC)" or the explicit
   *  ranking-data-limited line); otherwise "Schools: Strong|Mixed|Weak". */
  schoolBadge: string;
  /** Colour bucket for the school pill. */
  schoolBadgeTone: SchoolBadgeTone;
  /** Practical buy/rent price range (e.g. "Buy · House: A$900k–1.5M"). */
  priceBadge: string;
  /** Tiny qualifier of what that price usually buys (e.g.
   *  "Typical 3-bed family house", "Entry family-home range"). */
  priceContext: string;
}

/** How many suburbs the comparator renders. Hard cap to keep it scannable. */
export const WHY_TOP_N = 3;

/* -------------------------------------------------------------------------- */
/*  Axes — the angles a suburb can win or lose on                              */
/* -------------------------------------------------------------------------- */

type Axis =
  | "rail"
  | "schools"
  | "lifestyle"
  | "established"
  | "growth"
  | "value"
  | "quiet";

interface AxisScores {
  rail: number;
  schools: number;
  lifestyle: number;
  established: number;
  growth: number;
  value: number;
  quiet: number;
}

const ALL_AXES: Axis[] = [
  "rail",
  "schools",
  "lifestyle",
  "established",
  "growth",
  "value",
  "quiet",
];

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

function reasonsBlob(v: Verdict): string {
  return (v.reasons || []).join(" ");
}

/**
 * Fact refinement — detect when "rail" in the reason text refers to an
 * *adjacent* station reached by walk/drive, not an in-suburb station.
 * Without this, `extractFacts` flips hasRail true for any suburb whose
 * reasons mention a nearby line (e.g. Vermont: "Walk or 8-min drive to
 * Mitcham shops + train"), and the card ends up claiming "Train access"
 * as a positive while the tradeoff says "no direct train". This closes
 * that contradiction at the display layer.
 *
 * Returns true when the ONLY rail signal in the reasons is a trip-to
 * phrase (walk/drive to X station/shops + train). Never overrides the
 * positive `transit` tag — that's a structural signal, not a reason-string
 * guess.
 */
function isRailAdjacentOnly(v: Verdict): boolean {
  const t = tagSet(v);
  if (t.has("transit") || t.has("tram-corridor") || t.has("metro") || t.has("metro-tunnel")) {
    return false;
  }
  const r = reasonsBlob(v);
  const toRail =
    /\b\d+\s*[-–]?\s*min(?:ute)?s?\s+(?:walk|drive|bus)\s+to\b[^.;]*?(?:train|station|line)\b/i.test(r) ||
    /\b(?:walk|drive)\s+or\s+\d+\s*[-–]?\s*min(?:ute)?s?\s+(?:walk|drive)\s+to\b[^.;]*?(?:train|station|line)\b/i.test(r) ||
    /\bto\s+\w+\s+(?:shops\s+\+\s+(?:train|station)|station)\b/i.test(r);
  return toRail;
}

/**
 * Extract a commute-minutes range from the reason text. Returns the lo/hi
 * pair for a "45–55 min" phrase, or [n, n] for a single-value mention.
 * Used only for tradeoff phrasing — no scoring effect.
 */
function extractMinuteRange(v: Verdict): [number, number] | null {
  const r = reasonsBlob(v);
  const range = r.match(/(\d{1,3})\s*[–-]\s*(\d{1,3})\s*min\b/i);
  if (range) return [parseInt(range[1], 10), parseInt(range[2], 10)];
  const single = r.match(/(\d{1,3})\s*min\b/i);
  if (single) {
    const n = parseInt(single[1], 10);
    return [n, n];
  }
  return null;
}

/**
 * Score each axis for a single suburb. Positive = strength, negative =
 * absence/penalty. Unchanged from the prior implementation — this feeds
 * axis selection only, never shortlist ranking.
 */
function scoreAxes(v: Verdict): AxisScores {
  const t = tagSet(v);
  const facts = extractFacts(v);
  const sFact = getSchoolFact(v);

  let rail = 0;
  if (facts.transport.hasRail) rail += 5;
  if (facts.transport.terminus) rail += 2;
  if (facts.transport.commuteQuality === "short") rail += 3;
  else if (facts.transport.commuteQuality === "medium") rail += 1;
  else if (facts.transport.commuteQuality === "long") rail -= 2;
  if (facts.transport.busOnly) rail -= 3;
  if (t.has("cbd") || t.has("inner-city")) rail += 2;

  let schools = 0;
  if (sFact.tone === "strong") schools += 8;
  else if (sFact.tone === "mixed") schools += 4;
  else if (sFact.tone === "emerging") schools += 3;
  if (t.has("family") && sFact.tone === "strong") schools += 2;

  let lifestyle = 0;
  for (const k of ["nightlife", "cafe-culture", "food-scene", "bars"]) {
    if (t.has(k)) lifestyle += 2;
  }
  for (const k of ["beach", "waterfront", "entertainment", "cultural"]) {
    if (t.has(k)) lifestyle += 1;
  }
  for (const k of ["activity-centre", "retail-hub", "village"]) {
    if (t.has(k)) lifestyle += 1;
  }

  let established = 0;
  if (t.has("mature-infra")) established += 3;
  if (t.has("heritage")) established += 2;
  if (t.has("village")) established += 2;
  if (t.has("leafy")) established += 1;
  if (t.has("family")) established += 1;
  if (
    t.has("growth-corridor") ||
    t.has("estate") ||
    t.has("new-build") ||
    t.has("infra-lag")
  ) {
    established -= 4;
  }

  let growth = 0;
  if (t.has("growth-corridor")) growth += 4;
  if (t.has("estate") || t.has("new-build")) growth += 3;
  if (t.has("it-corridor")) growth += 2;
  if (t.has("infra-lag")) growth -= 1;
  if (t.has("mature-infra") || t.has("heritage")) growth -= 3;

  let value = 0;
  if (facts.price.band === "entry") value += 5;
  else if (facts.price.band === "mid") value += 2;
  else if (facts.price.band === "premium") value -= 2;

  let quiet = 0;
  if (t.has("leafy")) quiet += 2;
  if (t.has("low-amenity")) quiet += 2;
  if (t.has("village")) quiet += 1;
  if (t.has("nightlife") || t.has("bars") || t.has("cbd")) quiet -= 4;
  if (t.has("arterial-noise") || t.has("freeway-dependent")) quiet -= 2;

  return { rail, schools, lifestyle, established, growth, value, quiet };
}

/* -------------------------------------------------------------------------- */
/*  Distinguishing axis — what does THIS suburb beat its peers on?            */
/* -------------------------------------------------------------------------- */

interface Scored {
  v: Verdict;
  axes: AxisScores;
}

const PRIORITY_AXIS: Record<Priority, Axis | null> = {
  school: "schools",
  commute: "rail",
  lifestyle: "lifestyle",
  none: null,
};

/**
 * Pick the axis where this suburb most distances itself from the peer with
 * the next-best score on the same axis. Unchanged logic.
 */
function pickDistinguishingAxis(
  v: Verdict,
  myScores: AxisScores,
  scored: readonly Scored[],
  ctx: WhyContext,
  taken: ReadonlySet<Axis>
): Axis {
  const peerMax: Record<Axis, number> = {
    rail: -Infinity,
    schools: -Infinity,
    lifestyle: -Infinity,
    established: -Infinity,
    growth: -Infinity,
    value: -Infinity,
    quiet: -Infinity,
  };
  let havePeer = false;
  for (const s of scored) {
    if (s.v.id === v.id) continue;
    havePeer = true;
    for (const ax of ALL_AXES) {
      if (s.axes[ax] > peerMax[ax]) peerMax[ax] = s.axes[ax];
    }
  }
  if (!havePeer) return ownHighest(myScores, taken);

  const COMPETENT = 3;
  type Diff = { ax: Axis; diff: number; mine: number };
  const diffs: Diff[] = ALL_AXES.map((ax) => ({
    ax,
    diff: myScores[ax] - peerMax[ax],
    mine: myScores[ax],
  }));

  const eligible = diffs.filter((d) => d.mine >= COMPETENT && !taken.has(d.ax));
  eligible.sort((a, b) => {
    if (b.diff !== a.diff) return b.diff - a.diff;
    return b.mine - a.mine;
  });

  const pAx = PRIORITY_AXIS[ctx.priority];
  if (pAx && !taken.has(pAx)) {
    const pCand = eligible.find((d) => d.ax === pAx && d.mine >= 5);
    if (pCand) return pAx;
  }

  if (eligible.length === 0) return ownHighest(myScores, taken);

  const winning = eligible.find((d) => d.diff > 0);
  return (winning ?? eligible[0]).ax;
}

function ownHighest(s: AxisScores, taken: ReadonlySet<Axis> = new Set()): Axis {
  let best: Axis | null = null;
  let bestScore = -Infinity;
  for (const ax of ALL_AXES) {
    if (taken.has(ax)) continue;
    if (s[ax] > bestScore) {
      bestScore = s[ax];
      best = ax;
    }
  }
  if (best) return best;
  best = "value";
  bestScore = -Infinity;
  for (const ax of ALL_AXES) {
    if (s[ax] > bestScore) {
      bestScore = s[ax];
      best = ax;
    }
  }
  return best;
}

/* -------------------------------------------------------------------------- */
/*  Phrase tables — short, plain-English, no "is better if…"                   */
/* -------------------------------------------------------------------------- */

/** Positive phrase per axis. Fragment-style, 2–5 words. */
const POSITIVE_PHRASE: Record<Axis, string> = {
  rail: "Train access",
  schools: "Better public schools",
  lifestyle: "Cafes and walkable streets",
  established: "More established family area",
  growth: "Newer growth-corridor area",
  value: "Better value for families",
  quiet: "Quieter streets",
};

/** Secondary axis must reach this to be printed as a positive. */
const CLAIMABLE_FLOOR = 3;

/**
 * Pick a second positive axis — the next-best axis on this suburb that
 * (a) scores at or above the floor and (b) isn't the primary. Returns null
 * if nothing else is strong enough, in which case the caller fills with a
 * generic fact-grounded phrase.
 */
function pickSecondaryAxis(s: AxisScores, primary: Axis): Axis | null {
  let best: Axis | null = null;
  let bestScore = -Infinity;
  for (const ax of ALL_AXES) {
    if (ax === primary) continue;
    if (s[ax] < CLAIMABLE_FLOOR) continue;
    if (s[ax] > bestScore) {
      bestScore = s[ax];
      best = ax;
    }
  }
  return best;
}

/**
 * Fallback positive when the suburb has no second competent axis — pull a
 * plain fact from the verdict so users still get two bullets per card.
 */
function fallbackPositive(v: Verdict, primary: Axis, priority: Priority): string {
  const t = tagSet(v);
  const facts = extractFacts(v);
  if (primary !== "value" && facts.price.band === "entry") return "Lower entry prices";
  if (primary !== "rail" && facts.transport.hasRail && !isRailAdjacentOnly(v))
    return "Train access";
  if (primary !== "schools") {
    // Under School priority, never emit a vague "Strong public schools"
    // fallback. City-specific rank/proximity phrasing instead.
    if (priority === "school") {
      if (deriveCityFromVerdict(v) === "hyd") {
        const summary = proximitySummary(v.id, getActiveDataset() ?? []);
        if (summary.tier === "strong" || summary.tier === "mixed") {
          return proximityLine(summary);
        }
        // Weak proximity: skip so another fact wins the slot.
      } else {
        const topPublic = getPublicSchoolRanks(v.id, 1)[0];
        if (topPublic) return `Top public school (Rank ${topPublic.rank})`;
        // No public rank in MEL: skip this branch.
      }
    } else {
      const sFact = getSchoolFact(v);
      if (sFact.tone === "strong") return "Strong public schools";
    }
  }
  if (primary !== "established" && (t.has("leafy") || t.has("family")))
    return "Family-friendly streets";
  if (primary !== "lifestyle" && (t.has("cafe-culture") || t.has("food-scene")))
    return "Cafe strip nearby";
  if (primary !== "quiet" && t.has("leafy")) return "Quieter streets";
  return "Balanced overall";
}

/* -------------------------------------------------------------------------- */
/*  Tradeoff — fact-first, differentiated, uniqueness-enforced                 */
/* -------------------------------------------------------------------------- */

/**
 * Tradeoff "kinds" — a closed enum so uniqueness across the 3 cards can be
 * enforced (no two cards share a kind). Kinds are orthogonal frictions, so
 * forcing differentiation produces genuinely different tradeoff lines
 * rather than paraphrases of the same one.
 */
type TradeoffKind =
  | "long-commute-with-rail"  // rail in-suburb, but CBD trip is long
  | "no-in-suburb-rail"       // no direct train; drive/walk to the nearest
  | "bus-only"                // bus-only connection to rail
  | "price-premium"           // absolute-premium band
  | "price-higher-than-peers" // notably dearer than the others in THIS list
  | "school-weak"             // thin public-school pull
  | "school-emerging"         // secondary schools still forming
  | "infra-lag"               // infrastructure catching up to population
  | "flood-risk"              // low-lying streets flood
  | "bushfire-risk"           // urban-edge bushfire exposure
  | "road-noise"              // freeway/arterial noise
  | "nightlife-noise"         // lively-at-night tradeoff
  | "thin-stock"              // tightly held; patient wait
  | "retail-thin"             // no local retail strip
  | "outer-distance"          // outer-ring car-first fallback
  | "mild"                    // last-resort generic
  | "mild-balanced"           // variant so 2+ mild suburbs still differentiate
  | "mild-steady";            // ditto

interface TradeoffCandidate {
  kind: TradeoffKind;
  /** Higher = more concrete/factual; used to pick the best fit. */
  specificity: number;
  phrase: string;
}

/**
 * Build every tradeoff candidate that is *factually true* for this suburb.
 * Each candidate is gated by its own fact check — a suburb with in-suburb
 * rail can never produce a "no-in-suburb-rail" candidate, so the later
 * selector cannot contradict facts.
 *
 * Candidates also check the card's own positives so we don't name a
 * friction that clashes with a bullet just shown above ("rail is a
 * positive" → skip all rail-missing kinds).
 */
function buildTradeoffCandidates(
  v: Verdict,
  _ctx: WhyContext,
  positives: ReadonlySet<Axis>,
  peerPriceMids: readonly number[]
): TradeoffCandidate[] {
  const t = tagSet(v);
  const facts = extractFacts(v);
  const transport = facts.transport;
  const priceBand = facts.price.band;
  const sFact = getSchoolFact(v);
  const reasons = reasonsBlob(v);

  const adjacentOnly = isRailAdjacentOnly(v);
  const effectiveHasRail = transport.hasRail && !adjacentOnly;
  const range = extractMinuteRange(v);

  const C: TradeoffCandidate[] = [];

  // 1. Rail-IN-suburb but long commute — Mooroolbark-style.
  if (effectiveHasRail && (transport.commuteQuality === "long" || (range && range[1] >= 45))) {
    const rangeStr = range
      ? range[0] === range[1]
        ? `(~${range[0]} min)`
        : `(${range[0]}–${range[1]} min)`
      : transport.commuteMinutes
      ? `(~${transport.commuteMinutes} min)`
      : "";
    const phrase = rangeStr
      ? `has train access, but CBD commute is long ${rangeStr}`
      : "has train access, but CBD commute runs long";
    C.push({ kind: "long-commute-with-rail", specificity: 9, phrase });
  }

  // 2. No in-suburb rail (including adjacent-only mis-reads). Gate against
  //    a "rail" positive — no contradictions.
  if (!effectiveHasRail && !transport.busOnly && !positives.has("rail")) {
    const phrase = adjacentOnly
      ? "no direct train — walk or drive to the nearest station"
      : "no in-suburb train — commute relies on bus or car to rail";
    C.push({ kind: "no-in-suburb-rail", specificity: 8, phrase });
  }

  // 3. Bus-only connection.
  if (transport.busOnly && !positives.has("rail")) {
    C.push({
      kind: "bus-only",
      specificity: 8,
      phrase: "bus-only link to rail — slow at peak",
    });
  }

  // 4a. Absolute premium pricing.
  if (priceBand === "premium" && !positives.has("value")) {
    C.push({
      kind: "price-premium",
      specificity: 7,
      phrase: "house prices sit well above peer suburbs",
    });
  }

  // 4b. Relative to the other shortlisted picks — often the most decision-
  //     useful framing. Fires when this suburb's price mid is notably
  //     higher than the shortlist minimum, even if it doesn't clear the
  //     absolute "premium" threshold.
  const myMid = facts.price.mid;
  if (
    myMid !== null &&
    peerPriceMids.length > 0 &&
    !positives.has("value") &&
    priceBand !== "premium"
  ) {
    const peerMin = Math.min(...peerPriceMids);
    if (peerMin > 0 && myMid >= peerMin * 1.3) {
      C.push({
        kind: "price-higher-than-peers",
        specificity: 8,
        phrase: "house prices are notably higher than the other picks",
      });
    }
  }

  // 5. School frictions.
  if (sFact.tone === "limited" && !positives.has("schools")) {
    C.push({
      kind: "school-weak",
      specificity: 7,
      phrase: "thinner public-school options than the others",
    });
  }
  if (sFact.tone === "emerging" && !positives.has("schools")) {
    C.push({
      kind: "school-emerging",
      specificity: 6,
      phrase: "secondary schools still forming here",
    });
  }

  // 6. Named environmental risks — high specificity because concrete.
  if (t.has("flood-risk")) {
    C.push({ kind: "flood-risk", specificity: 7, phrase: "low-lying streets carry flood risk" });
  }
  if (t.has("bushfire-risk")) {
    C.push({ kind: "bushfire-risk", specificity: 7, phrase: "bushfire exposure on the urban edge" });
  }

  // 7. Infra lag (growth-corridor pain).
  if (t.has("infra-lag")) {
    C.push({
      kind: "infra-lag",
      specificity: 6,
      phrase: "infrastructure still catching up to the new-builds",
    });
  }

  // 8. Road noise — only if freeway/arterial actually adjacent.
  if (transport.freewayAdjacent) {
    C.push({
      kind: "road-noise",
      specificity: 5,
      phrase: "freeway-adjacent — arterial noise on main streets",
    });
  }

  // 9. Nightlife — only when lifestyle isn't a positive.
  if ((t.has("nightlife") || t.has("bars")) && !positives.has("lifestyle")) {
    C.push({
      kind: "nightlife-noise",
      specificity: 4,
      phrase: "lively late — weekends aren't quiet",
    });
  }

  // 10. Tightly-held / thin stock — parsed from reasons so it stays grounded.
  if (/\blow[- ]?stock\b|\bpatient\s+buyers|\bthin(?:\s+listing)?\s+stock\b|\b[4-9][-–]\d+\s*weeks\b/i.test(reasons)) {
    C.push({
      kind: "thin-stock",
      specificity: 5,
      phrase: "tightly held — patient 6–8 week wait typical",
    });
  }

  // 11. Retail-thin — reason text explicitly says "no retail strip" etc.
  if (/\bno\s+(?:real\s+)?retail\s+strip\b/i.test(reasons)) {
    C.push({
      kind: "retail-thin",
      specificity: 5,
      phrase: "no local retail strip — groceries/cafes a short drive",
    });
  }

  // 12. Outer-distance fallback — only if nothing commute-specific landed.
  const hasCommuteCandidate = C.some(
    (c) => c.kind === "long-commute-with-rail" || c.kind === "no-in-suburb-rail" || c.kind === "bus-only"
  );
  if (!hasCommuteCandidate && (t.has("growth-corridor") || transport.commuteQuality === "long")) {
    C.push({
      kind: "outer-distance",
      specificity: 4,
      phrase: "outer-ring location — most trips still need a car",
    });
  }

  // 13. Mild fallback — three variants so larger shortlists (4–5 cards)
  //     with multiple no-headline-friction suburbs can still differentiate.
  C.push({ kind: "mild", specificity: 1, phrase: "no single dealbreaker — tradeoffs are mild" });
  C.push({ kind: "mild-balanced", specificity: 1, phrase: "well-balanced — no headline weakness against peers" });
  C.push({ kind: "mild-steady", specificity: 1, phrase: "steady pick — nothing obvious that would rule it out" });

  return C;
}

/** Priority-aware boost so the friction matches the lens the user is using. */
function priorityBoost(kind: TradeoffKind, priority: Priority): number {
  if (priority === "commute") {
    if (kind === "long-commute-with-rail" || kind === "no-in-suburb-rail" || kind === "bus-only") return 3;
  }
  if (priority === "school") {
    if (kind === "school-weak" || kind === "school-emerging") return 3;
  }
  if (priority === "lifestyle") {
    if (kind === "nightlife-noise" || kind === "retail-thin") return 2;
  }
  return 0;
}

/**
 * Pick a tradeoff for this suburb:
 *   1. Build every factually true candidate.
 *   2. Sort by (specificity + priority boost), highest first.
 *   3. Prefer a kind not already claimed by an earlier card in the list.
 *   4. Fall back to the highest-ranked candidate if every one is taken —
 *      duplication is a last resort, never a default.
 */
function selectTradeoff(
  v: Verdict,
  ctx: WhyContext,
  positives: ReadonlySet<Axis>,
  taken: ReadonlySet<TradeoffKind>,
  peerPriceMids: readonly number[]
): { kind: TradeoffKind; phrase: string } {
  const candidates = buildTradeoffCandidates(v, ctx, positives, peerPriceMids);
  candidates.sort((a, b) => {
    const sa = a.specificity + priorityBoost(a.kind, ctx.priority);
    const sb = b.specificity + priorityBoost(b.kind, ctx.priority);
    return sb - sa;
  });

  for (const c of candidates) {
    if (!taken.has(c.kind)) return { kind: c.kind, phrase: c.phrase };
  }
  const best = candidates[0] ?? { kind: "mild" as const, phrase: "no single dealbreaker — tradeoffs are mild", specificity: 1 };
  return { kind: best.kind, phrase: best.phrase };
}

/* -------------------------------------------------------------------------- */
/*  Primary-shortlist copy — fact-first, uniqueness-enforced                   */
/* -------------------------------------------------------------------------- */

export interface PrimaryCardCopy {
  id: string;
  /** One distinct decision line — positive-anchored, fact-grounded. */
  reason: string;
  /** One distinct friction line — from the typed tradeoff candidate pool. */
  tradeoff: string;
}

/**
 * Generate decision + tradeoff copy for the PRIMARY "Top Picks for You"
 * shortlist cards. Reuses the same fact-first candidate pool the comparator
 * block uses, so the two surfaces stay honest with each other.
 *
 * Uniqueness is enforced across the whole passed-in list on BOTH lines —
 * no two cards share a decision axis, no two cards share a tradeoff kind
 * (duplication only as a last resort when every candidate is taken).
 *
 * Scoring / ranking / shortlist membership is NOT touched — callers must
 * rank first and then hand the ranked verdict list to this function; the
 * output preserves input order.
 */
export function decidePrimaryCards(
  list: readonly Verdict[],
  priority: Priority
): PrimaryCardCopy[] {
  if (list.length === 0) return [];

  const scored: Scored[] = list.map((v) => ({ v, axes: scoreAxes(v) }));
  const takenPrimary = new Set<Axis>();
  const takenTradeoffs = new Set<TradeoffKind>();
  const peerPriceMids = list
    .map((v) => extractFacts(v).price.mid)
    .filter((n): n is number => typeof n === "number");

  // selectTradeoff only reads `priority` off its ctx — the other fields are
  // placeholders so we can reuse the comparator's function without widening
  // its signature.
  const narrowCtx: WhyContext = {
    priority,
    budgetBand: "none",
    city: "mel",
    purchaseMode: "buy",
    propertyType: "any",
  };

  return scored.map(({ v, axes }) => {
    const primary =
      scored.length > 1
        ? pickDistinguishingAxis(v, axes, scored, narrowCtx, takenPrimary)
        : ownHighest(axes);
    takenPrimary.add(primary);

    const reason = resolvePositive(primary, v, priority);
    const { kind, phrase } = selectTradeoff(
      v,
      narrowCtx,
      new Set<Axis>([primary]),
      takenTradeoffs,
      peerPriceMids
    );
    takenTradeoffs.add(kind);

    return { id: v.id, reason, tradeoff: phrase };
  });
}

/* -------------------------------------------------------------------------- */
/*  Public                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Build the decision-table rows for the shortlist. Caps at top-3 input,
 * returns one WhyLine per suburb with two positives + one tradeoff.
 *
 * Shortlist order is preserved — this module never reranks.
 */
export function explainShortlist(
  list: readonly Verdict[],
  ctx: WhyContext
): WhyLine[] {
  if (list.length === 0) return [];

  const top = list.slice(0, WHY_TOP_N);
  const scored: Scored[] = top.map((v) => ({ v, axes: scoreAxes(v) }));

  // Primary-axis uniqueness across the 3 cards keeps the lead strengths
  // differentiating — no two suburbs open with the same bullet.
  const takenPrimary = new Set<Axis>();

  // Tradeoff-kind uniqueness across the 3 cards — forces differentiated
  // frictions so the shortlist reads as 3 distinct decisions rather than
  // 3 paraphrases of the same caveat.
  const takenTradeoffs = new Set<TradeoffKind>();

  // Price mids across the shortlist — powers the "higher-priced than the
  // other picks" relative candidate. Unitless: comparisons are within the
  // same (city, mode) context so the per-sqm units match.
  const peerPriceMids = top
    .map((v) => extractFacts(v).price.mid)
    .filter((n): n is number => typeof n === "number");

  return scored.map(({ v, axes }) => {
    const primary =
      scored.length > 1
        ? pickDistinguishingAxis(v, axes, scored, ctx, takenPrimary)
        : ownHighest(axes);
    takenPrimary.add(primary);

    const secondary = pickSecondaryAxis(axes, primary);
    const positiveAxes = new Set<Axis>([primary]);
    if (secondary) positiveAxes.add(secondary);

    const pos1 = resolvePositive(primary, v, ctx.priority);
    const pos2 = secondary
      ? resolvePositive(secondary, v, ctx.priority)
      : fallbackPositive(v, primary, ctx.priority);

    const { kind, phrase } = selectTradeoff(
      v,
      ctx,
      positiveAxes,
      takenTradeoffs,
      peerPriceMids
    );
    takenTradeoffs.add(kind);

    const badge = schoolBadgeFor(v, ctx.priority);
    return {
      id: v.id,
      name: v.name,
      positives: [pos1, pos2],
      tradeoff: phrase,
      schoolBadge: badge.text,
      schoolBadgeTone: badge.tone,
      priceBadge: priceBadgeFor(v, ctx),
      priceContext: priceContextFor(v, ctx),
    };
  });
}

/**
 * Resolve a positive phrase for the chosen axis.
 *
 * Dynamic cases:
 *   - rail + `isRailAdjacentOnly` → downgrade to "Short drive to train".
 *   - schools under Priority === "school":
 *       • rank data available → "Top schools (Rank X)" with best rank.
 *       • no rank data         → the explicit "ranking data limited" line
 *                                (NEVER fall back to vague phrasing).
 */
function resolvePositive(axis: Axis, v: Verdict, priority: Priority): string {
  if (axis === "rail" && isRailAdjacentOnly(v)) return "Short drive to train";
  if (axis === "schools" && priority === "school") {
    // City-specific school model. Hyderabad doesn't use public zoning —
    // the relevant framing is proximity to good schools, not "zone rank".
    if (deriveCityFromVerdict(v) === "hyd") {
      const summary = proximitySummary(v.id, getActiveDataset() ?? []);
      return proximityLine(summary);
    }
    // Melbourne: only PUBLIC ranks earn the "suburb zone advantage"
    // framing. Privates are shown elsewhere but never imply zoning.
    const topPublic = getPublicSchoolRanks(v.id, 1)[0];
    if (topPublic) return `Top public school (Rank ${topPublic.rank})`;
    if (getPrivateSchoolRanks(v.id, 1).length > 0) return PRIVATE_ONLY_NOTE;
    return SCHOOL_RANK_UNKNOWN_LINE;
  }
  return POSITIVE_PHRASE[axis];
}

/* -------------------------------------------------------------------------- */
/*  Badge helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * School badge — rank-first under School priority, tier-based otherwise.
 *
 * Under Priority === "school":
 *   - "Schools: Rank 4"        (single known rank)
 *   - "Schools: Rank 4–9 (VIC)" (2+ ranks; range)
 *   - "Ranking data limited"    (no data — no vague fallback)
 *
 * Tint is driven by best-rank when present, by school tone otherwise.
 */
function schoolBadgeFor(
  v: Verdict,
  priority: Priority
): { text: string; tone: SchoolBadgeTone } {
  if (priority === "school") {
    // ---- HYD: proximity model ------------------------------------
    if (deriveCityFromVerdict(v) === "hyd") {
      const summary = proximitySummary(v.id, getActiveDataset() ?? []);
      const toneByTier: Record<ProximityTier, SchoolBadgeTone> = {
        strong: "strong",
        mixed: "mixed",
        weak: "weak",
        unknown: "unknown",
      };
      return { text: proximityBadge(summary), tone: toneByTier[summary.tier] };
    }
    // ---- MEL: public-rank zone model -----------------------------
    // The badge is about suburb zone advantage — public only. A suburb
    // with only private ranks gets a distinct "Private only" badge so
    // the user is never tricked into reading it as a public-zone plus.
    const publicRange = getPublicSchoolRankRange(v.id, 2);
    if (publicRange) {
      const text =
        publicRange.count === 1 || publicRange.min === publicRange.max
          ? `Public Rank ${publicRange.min} (VIC)`
          : `Public Rank ${publicRange.min}–${publicRange.max} (VIC)`;
      const tone: SchoolBadgeTone =
        publicRange.min <= 30 ? "strong" : publicRange.min <= 100 ? "mixed" : "weak";
      return { text, tone };
    }
    if (getPrivateSchoolRanks(v.id, 1).length > 0) {
      return { text: "Private schools only", tone: "unknown" };
    }
    if (hasSchoolRank(v.id)) {
      return { text: SCHOOL_RANK_UNKNOWN_LINE, tone: "unknown" };
    }
    return { text: SCHOOL_RANK_UNKNOWN_LINE, tone: "unknown" };
  }
  const t = getSchoolFact(v).tone;
  if (t === "strong") return { text: "Schools: Strong", tone: "strong" };
  if (t === "limited") return { text: "Schools: Weak", tone: "weak" };
  return { text: "Schools: Mixed", tone: "mixed" };
}

/**
 * Practical price badge. Users don't think in A$/m² — they think in total
 * house price (Buy) or weekly rent (Rent), usually keyed to the property
 * type they've selected. This maps the suburb's per-sqm price band to the
 * same band table the rest of the app uses and formats the result with a
 * short, mode-aware prefix:
 *
 *   Buy + house      → "Buy · House: A$900k–1.5M"
 *   Buy + apartment  → "Buy · Apartment: A$550k–900k"
 *   Buy + any        → "Buy: A$900k–1.5M"
 *   Rent (any type)  → "Rent: $600–850/wk"
 */
const PRICE_BAND_MAP: Record<PriceBand, Exclude<BudgetBand, "none">> = {
  entry: "tight",
  mid: "moderate",
  premium: "stretch",
  unknown: "moderate",
};

/**
 * Minimal context required to format a practical price label/subtitle.
 * A narrower type than WhyContext so non-comparator consumers (the verdict
 * card popup, the compare card) don't have to fake priority/budget fields.
 */
export interface PracticalPriceCtx {
  city: CityCode;
  purchaseMode: PurchaseMode;
  propertyType: PropertyType;
}

/**
 * "Buy · House: A$900k–1.5M" / "Buy · Apartment: A$550k–900k" /
 * "Buy: A$900k–1.5M" / "Rent: $600–850/wk" — the same practical price
 * string used on the shortlist pill, exported so the verdict-card popup
 * and compare card stay aligned.
 */
export function practicalPriceLabel(v: Verdict, ctx: PracticalPriceCtx): string {
  return priceBadgeFor(v, {
    priority: "none",
    budgetBand: "none",
    city: ctx.city,
    purchaseMode: ctx.purchaseMode,
    propertyType: ctx.propertyType,
  });
}

/**
 * Tiny qualifier — "Typical 3-bed family house", "Entry family-home range",
 * etc. Same generator as the shortlist comparator.
 */
export function practicalPriceContext(v: Verdict, ctx: PracticalPriceCtx): string {
  return priceContextFor(v, {
    priority: "none",
    budgetBand: "none",
    city: ctx.city,
    purchaseMode: ctx.purchaseMode,
    propertyType: ctx.propertyType,
  });
}

/**
 * Derive the active city from a verdict id. Datasets use a city-prefixed
 * id for non-HYD cities (`mel-*`, `blr-*`, `syd-*`); unprefixed ids are
 * Hyderabad. Keeps callers from having to thread city through when they
 * can infer it from the verdict they're already rendering.
 */
export function deriveCityFromVerdict(v: Verdict): CityCode {
  if (v.id.startsWith("mel-")) return "mel";
  if (v.id.startsWith("blr-")) return "blr";
  if (v.id.startsWith("syd-")) return "syd";
  return "hyd";
}

function priceBadgeFor(v: Verdict, ctx: WhyContext): string {
  const suburbBand = PRICE_BAND_MAP[extractFacts(v).price.band];
  const fmtCtx = {
    city: ctx.city,
    mode: ctx.purchaseMode,
    type: ctx.propertyType,
  };
  // When the user has picked a narrower bucket, collapse the suburb's
  // band × bucket intersection to a single "decision price" (midpoint,
  // prefixed with "~"). Users asked for "what will I actually pay?" —
  // a range of A$600k–700k still reads as uncertainty; ~A$650k reads as
  // a commitment. When the clamp yields no overlap we fall back to the
  // band label so something sensible renders.
  //
  // When the suburb carries researched `priceData` (live medians from
  // property.com.au / realestate.com.au / CoreLogic for MEL, or
  // MagicBricks / 99acres for HYD), `clampedPriceRange` centres the
  // window on that median and delivers a tighter, truer decision price
  // — even when the user is on "Any" bucket. We prefer that over the
  // coarse band label whenever possible so premium or undervalued
  // suburbs don't all collapse into the same "A$1.5M–3M+" stripe.
  const bucket = getBudgetBucket();
  const clamped = clampedPriceRange(fmtCtx, suburbBand, bucket, v.priceData);
  let range: string;
  if (bucket === "any") {
    // With priceData we have a real anchor — show the tight decision
    // price. Without it, keep the original band label (the "Any" bucket
    // signal is "user hasn't committed to a price yet").
    range = v.priceData && clamped
      ? formatMidpoint(fmtCtx, clamped)
      : budgetRangeLabel(fmtCtx, suburbBand);
  } else {
    range = clamped
      ? formatMidpoint(fmtCtx, clamped)
      : budgetRangeLabel(fmtCtx, suburbBand);
  }

  if (ctx.purchaseMode === "rent") return `Rent: ${range}`;
  if (ctx.propertyType === "house") return `Buy · House: ${range}`;
  if (ctx.propertyType === "apartment") return `Buy · Apartment: ${range}`;
  return `Buy: ${range}`;
}

/**
 * Tiny one-line qualifier describing what the price above *usually buys* —
 * the bed-count + segment most users map a band to. Keeps the badge honest
 * without adding a paragraph: "A$900k–1.5M" alone leaves users guessing
 * between a renovator and a 4-bed new-build; "Typical 3-bed family house"
 * anchors the expectation.
 *
 * Derived from (mode, property-type, suburb price band). No new scoring —
 * just a phrasing table keyed to the same band the price badge uses.
 */
function priceContextFor(v: Verdict, ctx: WhyContext): string {
  const band = PRICE_BAND_MAP[extractFacts(v).price.band];
  const isApartment = ctx.propertyType === "apartment";

  if (ctx.purchaseMode === "rent") {
    if (isApartment) {
      if (band === "tight") return "Studio or 1-bed apartment";
      if (band === "stretch") return "Large 3-bed apartment rental";
      return "Typical 2-bed apartment rental";
    }
    if (band === "tight") return "Entry 2–3 bed house rental";
    if (band === "stretch") return "Premium 4-bed house rental";
    return "Typical 3-bed family rental";
  }

  // Buy
  if (isApartment) {
    if (band === "tight") return "Entry 1-bed apartment";
    if (band === "stretch") return "Large 3-bed apartment";
    return "Typical 2-bed apartment";
  }
  if (band === "tight") return "Entry family-home range";
  if (band === "stretch") return "Premium 4-bed house";
  return "Typical 3-bed family house";
}
